import { SessionContext } from '@/context/SessionContext';
import ConductionAPI from '@/javascript/conduction-api';
import { Utils } from '@/javascript/utils';
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconAt } from '@tabler/icons-react';
import { useContext, useState } from 'react';

interface ForgotPasswordModalProps {
	showModal: boolean;
	setShowModal: (show: boolean) => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = (props) => {
	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("Modals must be a child of sessionContext.");
	}

	const { showModal, setShowModal } = props;

	const [isSubmitting, setIsSubmitting] = useState(false);

	const form = useForm({
		initialValues: {
			email: ''
		},
		validate: {
			email: (value: string) => !/^\S+@\S+\.\S+$/.test(value) ? 'Invalid email address' : null,
		},
	});

	const handleSubmit = async () => {
		if (form.validate().hasErrors) {
			return;
		}

		setIsSubmitting(true);
		const success = await ConductionAPI.requestPasswordReset(form.values.email);
		if (success) {
			Utils.displayNotification("Password Reset Requested", "If an account with that email exists, a password reset link has been sent.", "green", <IconAlertCircle size={16} />);
			setShowModal(false);
		} else {
			Utils.displayNotification("Error", "Failed to request password reset. Please try again.", "red", <IconAlertCircle size={16} />);
		}
		setIsSubmitting(false);
	};

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="Forgot Password"
			centered
			size="md"
			styles={{
				title: {
					fontSize: 26,
					fontVariant: "all-small-caps"
				}
			}}
			zIndex={101} // In front of LoginModal
		>
			<Stack>
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack>
						<Text size="lg">
							Enter your email address to receive a password reset link.
						</Text>

						<TextInput
							label={
								<Group>
									<IconAt size={20} />
									<Text>Email</Text>
								</Group>
							}
							{...form.getInputProps('email')}
							required
							withAsterisk={false}
							disabled={isSubmitting}
						/>

						<Button
							type="submit"
							radius="xl"
							size='sm'
							loading={isSubmitting}
						>
							Send Email
						</Button>
					</Stack>
				</form>
			</Stack>
		</Modal>
	);
};

export default ForgotPasswordModal;
